import {z} from 'zod';

import {getApiDateString, parseDate} from 'shared/web';

// ============================================================================
// SHARED PRIMITIVES & ENUMS
// ============================================================================

export const VehicleDataDecimalSchema = z.string();

export const ValueTypeSchema = z.enum([
  'integer',
  'decimal',
  'string',
  'money',
  'date',
  'datetime',
  'boolean',
  'enum',
]);

export const VehicleTypeEnumSchema = z.enum([
  'VEHICLETYPE_AGRICULTURAL_MACHINE',
  'VEHICLETYPE_CONSTRUCTION_MACHINE',
  'VEHICLETYPE_CARAVAN',
  'VEHICLETYPE_PASSENGER_CAR',
  'VEHICLETYPE_VAN',
  'VEHICLETYPE_MOTORCYCLE',
  'VEHICLETYPE_TRUCK',
  'VEHICLETYPE_SEMI_TRUCK',
  'VEHICLETYPE_SEMI_TRAILER',
  'VEHICLETYPE_TRAILER',
  'VEHICLETYPE_BUS',
  'VEHICLETYPE_OTHER',
]);

export const FuelTypeEnumSchema = z.enum([
  'FUELTYPE_PETROL',
  'FUELTYPE_DIESEL',
  'FUELTYPE_ELECTRIC',
  'FUELTYPE_LPG',
  'FUELTYPE_CNG',
  'FUELTYPE_HYBRID',
  'FUELTYPE_HYDROGEN',
  'FUELTYPE_ETHANOL',
  'FUELTYPE_OTHER',
  'FUELTYPE_LNG',
]);

export const HybridTypeEnumSchema = z.enum([
  'HYBRIDTYPE_FULL',
  'HYBRIDTYPE_PHEV',
  'HYBRIDTYPE_MHEV',
  'HYBRIDTYPE_MICRO',
]);

export const ConditionEnumSchema = z.enum([
  'CONDITION_NEW',
  'CONDITION_USED',
  'CONDITION_DEMONSTRATION_CAR',
  'CONDITION_DAMAGED',
  'CONDITION_CLASSIC_CAR',
  'CONDITION_SPARE_PARTS',
]);

// ============================================================================
// SHARED COMPONENT SCHEMAS
// ============================================================================

// ----------------------------------------------------------------------------
// Dimensions
// ----------------------------------------------------------------------------
export const DimensionsV2ResponseBodySchema = z.object({
  width: z.string().nullable().optional(),
  height: z.string().nullable().optional(),
  length: z.string().nullable().optional(),
});

export const DimensionsRequestBodySchema = z.object({
  width: VehicleDataDecimalSchema.nullable().optional(),
  height: VehicleDataDecimalSchema.nullable().optional(),
  length: VehicleDataDecimalSchema.nullable().optional(),
});

// ----------------------------------------------------------------------------
// Colors
// ----------------------------------------------------------------------------
export const ColorBaseSchema = z.object({
  value: z.string().nullable(),
  code: z.string().nullable(),
  name: z.string().nullable(),
});

export const ColorV2ResponseBodySchema = ColorBaseSchema;
export const ColorRequestBodySchema = ColorBaseSchema;

// ----------------------------------------------------------------------------
// Vehicle Features
// ----------------------------------------------------------------------------
export const VehicleFeatureV2ResponseBodySchema = z.object({
  translation: z.string(),
  key: z.string(),
  isHighlighted: z.boolean(),
  isPremium: z.boolean(),
});

export const FeatureRequestBodySchema = z.object({
  key: z.string(),
  isHighlighted: z.boolean().nullable().optional(),
  isPremium: z.boolean().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Service Intervals
// ----------------------------------------------------------------------------
const ServiceIntervalBaseSchema = z.object({
  lastReplacementDate: z.string().nullable(),
  lastReplacementMileage: z.number().nullable(),
  nextReplacementDate: z.string().nullable(),
  nextReplacementMileage: z.number().nullable(),
});

const ServiceIntervalWithReplacementSchema = ServiceIntervalBaseSchema.extend({
  replacementAfterMonths: z.number().nullable(),
  replacementAfterMileage: z.number().nullable(),
});

export const VehicleServiceIntervalsServiceInspectionSchemaVehicleOperations = z.object({
  lastServiceInspectionDate: z.string().nullable(),
  lastServiceInspectionMileage: z.number().nullable(),
  nextServiceInspectionDate: z.string().nullable(),
  nextServiceInspectionMileage: z.number().nullable(),
  replacementAfterMonths: z.number().nullable(),
  replacementAfterMileage: z.number().nullable(),
});

export const VehicleServiceIntervalsEngineOilSchemaVehicleOperations =
  ServiceIntervalWithReplacementSchema;
export const VehicleServiceIntervalsGearboxOilSchemaVehicleOperations =
  ServiceIntervalWithReplacementSchema;
export const VehicleServiceIntervalsFourByFourOilDistributorSchemaVehicleOperations =
  ServiceIntervalWithReplacementSchema;
export const VehicleServiceIntervalsTimingBeltSchemaVehicleOperations =
  ServiceIntervalWithReplacementSchema;
export const VehicleServiceIntervalsBrakeFluidSchemaVehicleOperations = ServiceIntervalBaseSchema;
export const VehicleServiceIntervalsCoolantFluidSchemaVehicleOperations = ServiceIntervalBaseSchema;

export const VehicleServiceIntervalsAnnualMileageSchemaVehicleOperations = z.object({
  calculatedFromServiceVisits: z.number().nullable(),
});

export const VehicleServiceIntervalsSchemaVehicleOperations = z.object({
  serviceInspection: VehicleServiceIntervalsServiceInspectionSchemaVehicleOperations,
  engineOil: VehicleServiceIntervalsEngineOilSchemaVehicleOperations,
  gearboxOil: VehicleServiceIntervalsGearboxOilSchemaVehicleOperations,
  fourByFourOilDistributor: VehicleServiceIntervalsFourByFourOilDistributorSchemaVehicleOperations,
  timingBelt: VehicleServiceIntervalsTimingBeltSchemaVehicleOperations,
  brakeFluid: VehicleServiceIntervalsBrakeFluidSchemaVehicleOperations,
  coolantFluid: VehicleServiceIntervalsCoolantFluidSchemaVehicleOperations,
  annualMileage: VehicleServiceIntervalsAnnualMileageSchemaVehicleOperations.nullable(),
});

// ----------------------------------------------------------------------------
// Typed Values & Custom Fields
// ----------------------------------------------------------------------------
export const TypedAnyValueSchema = z.object({
  type: ValueTypeSchema,
  value: z.union([
    z.array(z.any()),
    z.string(),
    z.number(),
    z.boolean(),
    z.object({
      amount: z.string(),
      currency: z.string(),
    }),
  ]),
});

export const FieldValueSchema = z.object({
  definitionId: z.string(),
  value: TypedAnyValueSchema.nullable(),
});

// ----------------------------------------------------------------------------
// Electric Vehicle Battery
// ----------------------------------------------------------------------------
const ElectricVehicleBatteryBaseSchema = z.object({
  chargingAC: z.string().nullable(),
  chargingDC: z.string().nullable(),
  batteryCapacityWh: z.string().nullable(),
  batteryCapacityAh: z.string().nullable(),
  batteryVoltage: z.string().nullable(),
});

export const ElectricVehicleBatteryV2ResponseBodySchema = ElectricVehicleBatteryBaseSchema;
export const ElectricVehicleBatteryRequestBodySchema = ElectricVehicleBatteryBaseSchema.partial();

// ████████████████████████████████████████████████████████████████████████████
// ████████████████████████████████████████████████████████████████████████████
// ██                                                                        ██
// ██                          BASE VEHICLE TYPES                           ██
// ██                                                                        ██
// ████████████████████████████████████████████████████████████████████████████
// ████████████████████████████████████████████████████████████████████████████

// ============================================================================
// BASE VEHICLE FIELD GROUPS
// ============================================================================

// ----------------------------------------------------------------------------
// Identification & Metadata
// ----------------------------------------------------------------------------
export const BaseVehicleIdentificationSchema = z.object({
  id: z.string(),
  externalId: z.string().nullable().optional(),
  publicId: z.string().nullable().optional(),
  customId: z.string().nullable().optional(),
  title: z.string(),
});

export const BaseVehicleMetadataSchema = z.object({
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  isArchived: z.boolean().nullable().optional(),
  keyLocation: z.string().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Basic Information
// ----------------------------------------------------------------------------
export const BaseVehicleBasicInfoSchema = z.object({
  make: z.string().nullable().optional(),
  modelFamily: z.string().nullable().optional(),
  modelFamilyGroup: z.string().nullable().optional(),
  trim: z.string().nullable().optional(),
  variant: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  vehicle_type: z.string().nullable().optional(),
  condition: ConditionEnumSchema.nullable().optional(),
  customMake: z.string().nullable().optional(),
  customModelFamily: z.string().nullable().optional(),
  truckHomologation: z.boolean().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Technical Specifications
// ----------------------------------------------------------------------------
export const BaseVehicleTechnicalSpecsSchema = z.object({
  vin: z.string().nullable().optional(),
  bodyStyle: z.string().nullable().optional(),
  doorCountCategory: z.string().nullable().optional(),
  seatCount: z.number().nullable().optional(),
  standingPlaceCount: z.number().nullable().optional(),
  bedCount: z.number().nullable().optional(),
  axleCount: z.number().nullable().optional(),
  dimensions: DimensionsV2ResponseBodySchema.nullable().optional(),
  dimensionsLoadingSpace: DimensionsV2ResponseBodySchema.nullable().optional(),
  weight: z.number().nullable().optional(),
  operatingWeight: z.number().nullable().optional(),
  maximalLoadCapacity: z.number().nullable().optional(),
  loadingCapacity: z.number().nullable().optional(),
  loadingArea: z.string().nullable().optional(),
  wheelBase: z.number().nullable().optional(),
  trunkSpace: z.number().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Engine Specifications
// ----------------------------------------------------------------------------
export const BaseVehicleEngineSpecsSchema = z.object({
  engineCode: z.string().nullable().optional(),
  engineName: z.string().nullable().optional(),
  engineNumber: z.string().nullable().optional(),
  engineVolume: z.number().nullable().optional(),
  cylinderCount: z.number().nullable().optional(),
  power: z.number().nullable().optional(),
  maximalPower: z.number().nullable().optional(),
  fiscalPower: z.number().nullable().optional(),
  isFiscalPowerManualOverride: z.boolean().nullable().optional(),
  maxTorque: z.string().nullable().optional(),
  fuelType: FuelTypeEnumSchema.nullable().optional(),
  secondaryFuelType: FuelTypeEnumSchema.nullable().optional(),
  hybridType: HybridTypeEnumSchema.nullable().optional(),
  transmission: z.string().nullable().optional(),
  transmissionNumber: z.string().nullable().optional(),
  transmissionCode: z.string().nullable().optional(),
  gearCount: z.number().nullable().optional(),
  drive: z.string().nullable().optional(),
  handDriveType: z.string().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Consumption & Emissions
// ----------------------------------------------------------------------------
export const Co2EmissionClassEnumSchema = z.enum(['A', 'B', 'C', 'D', 'E', 'F', 'G']);

export const BaseVehicleConsumptionSchema = z.object({
  // NEDC consumption fields
  fuelConsumptionCombined: z.string().nullable().optional(),
  fuelConsumptionExtraUrban: z.string().nullable().optional(),
  fuelConsumptionUrban: z.string().nullable().optional(),
  // WLTP consumption fields (liquid/gas)
  fuelConsumptionWltpCombined: z.string().nullable().optional(),
  fuelConsumptionWltpCityCenter: z.string().nullable().optional(),
  fuelConsumptionWltpOutskirts: z.string().nullable().optional(),
  fuelConsumptionWltpCountryRoads: z.string().nullable().optional(),
  fuelConsumptionWltpHighway: z.string().nullable().optional(),
  fuelConsumptionWltpWeighted: z.string().nullable().optional(),
  // WLTP consumption fields (electric)
  fuelConsumptionWltpCombinedKwh: z.string().nullable().optional(),
  fuelConsumptionWltpCityCenterKwh: z.string().nullable().optional(),
  fuelConsumptionWltpOutskirtsKwh: z.string().nullable().optional(),
  fuelConsumptionWltpCountryRoadsKwh: z.string().nullable().optional(),
  fuelConsumptionWltpHighwayKwh: z.string().nullable().optional(),
  fuelConsumptionWltpWeightedCombinedKwh: z.string().nullable().optional(),
  // Power consumption fields
  powerConsumptionCombined: z.string().nullable().optional(),
  powerCombustionEngine: z.string().nullable().optional(),
  powerElectricEngine: z.string().nullable().optional(),
  // Emissions
  carbonDioxideEmission: z.string().nullable().optional(),
  carbonDioxideEmissionDischarged: z.string().nullable().optional(),
  carbonDioxideEmissionWeightedCombined: z.string().nullable().optional(),
  carbonDioxideEmissionClass: Co2EmissionClassEnumSchema.nullable().optional(),
  carbonDioxideEmissionClassDischarged: Co2EmissionClassEnumSchema.nullable().optional(),
  carbonDioxideEmissionClassWeightedCombined: Co2EmissionClassEnumSchema.nullable().optional(),
  emissionClass: z.string().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Electric Vehicle Specifications
// ----------------------------------------------------------------------------
export const BaseVehicleElectricSpecsSchema = z.object({
  chargingAC: z.number().nullable().optional(),
  chargingTimeAC: z.number().nullable().optional(),
  chargingDC: z.number().nullable().optional(),
  chargingTimeDC: z.number().nullable().optional(),
  batteryType: z.string().nullable().optional(),
  batteryCapacityWh: z.number().nullable().optional(),
  batteryCapacityAh: z.number().nullable().optional(),
  batteryVoltage: z.number().nullable().optional(),
  batteryCount: z.number().nullable().optional(),
  chargingConnectorType: z.string().nullable().optional(),
  biDirectionalCharging: z.string().nullable().optional(),
  electricRange: z.number().nullable().optional(),
  electricRangeCity: z.number().nullable().optional(),
  combinedRange: z.number().nullable().optional(),
  combinedRangeCity: z.number().nullable().optional(),
  batteryMileageLimit: z.number().nullable().optional(),
  batteryWarrantyUntil: z.string().nullable().optional(),
  batteryOwnershipType: z.string().nullable().optional(),
  hasRangeExtender: z.boolean().nullable().optional(),
  electricEngineNote: z.string().nullable().optional(),
  electricVehicleBattery: ElectricVehicleBatteryV2ResponseBodySchema.nullable().optional(),
  batteryCondition: z.string().nullable().optional(),
  batteryConditionMeasuredAt: z.string().nullable().optional(),
  actualBatteryRange: z.number().nullable().optional(),
  batteryCertificate: z.boolean().nullable().optional(),
  certificateFileId: z.string().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Appearance & Features
// ----------------------------------------------------------------------------
export const BaseVehicleAppearanceSchema = z.object({
  exteriorColor: ColorV2ResponseBodySchema.nullable().optional(),
  exteriorColorFactoryName: z.string().nullable().optional(),
  exteriorColorSpecification: z.string().nullable().optional(),
  manufacturerColorCode: z.string().nullable().optional(),
  manufacturerColorName: z.string().nullable().optional(),
  interiorColor: ColorV2ResponseBodySchema.nullable().optional(),
  interiorColorName: z.string().nullable().optional(),
  interiorMaterial: z.string().nullable().optional(),
  features: z.array(VehicleFeatureV2ResponseBodySchema).nullable().optional(),
  featuresByManufacturer: z.string().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Registration & Origin
// ----------------------------------------------------------------------------
export const BaseVehicleRegistrationSchema = z.object({
  isRegistered: z.boolean().nullable().optional(),
  registrationPlate: z.string().nullable().optional(),
  vehicleRegistrationNumber: z.string().nullable().optional(),
  registrationCertificateNumber: z.string().nullable().optional(),
  firstRegistrationCountry: z.string().nullable().optional(),
  firstRegistrationOn: z.string().nullable().optional(),
  lastRegistrationCountry: z.string().nullable().optional(),
  originCountry: z.string().nullable().optional(),
  originCountryName: z.string().nullable().optional(),
  manufacturedOn: z.string().nullable().optional(),
  modelYear: z.string().nullable().optional(),
  ownerCount: z.number().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Service & Maintenance
// ----------------------------------------------------------------------------
export const BaseVehicleServiceSchema = z.object({
  mileage: z.number().nullable().optional(),
  realMileage: z.number().nullable().optional(),
  lastServiceDate: z.string().nullable().optional(),
  nextServiceDate: z.string().nullable().optional(),
  lastServiceMileage: z.number().nullable().optional(),
  nextServiceMileage: z.number().nullable().optional(),
  serviceBookType: z.string().nullable().optional(),
  serviceBookState: z.string().nullable().optional(),
  servicedInAuthorizedNetwork: z.boolean().nullable().optional(),
  serviceContractNote: z.string().nullable().optional(),
  serviceContractUntil: z.string().nullable().optional(),
  serviceContractMileageLimit: z.number().nullable().optional(),
  serviceIntervals: VehicleServiceIntervalsSchemaVehicleOperations.nullable().optional(),
});

// ----------------------------------------------------------------------------
// Inspections & Certifications
// ----------------------------------------------------------------------------
export const BaseVehicleInspectionSchema = z.object({
  technicalInspectionNote: z.string().nullable().optional(),
  technicalInspectionValidSince: z.string().nullable().optional(),
  technicalInspectionValidUntil: z
    .string()
    .nullable()
    .optional()
    .transform((value) => {
      if (value == null) {
        return value;
      }
      const parsed = parseDate(value);
      return parsed ? getApiDateString(parsed) : value;
    }),
  isAcceptableForTechnicalInspection: z.boolean().nullable().optional(),
  gasRevisionValidUntil: z.string().nullable().optional(),
  pressureVesselValidUntil: z.string().nullable().optional(),
  hasCoc: z.boolean().nullable().optional(),
  hasDpf: z.boolean().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Warranty Information
// ----------------------------------------------------------------------------
export const BaseVehicleWarrantySchema = z.object({
  warrantyDate: z.string().nullable().optional(),
  warrantyMileage: z.number().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Commercial Vehicle Specifications
// ----------------------------------------------------------------------------
export const BaseVehicleCommercialSchema = z.object({
  europalletStorageSpaces: z.number().nullable().optional(),
  lowDeck: z.boolean().nullable().optional(),
  maximalAxleLoad: z.number().nullable().optional(),
  maximalRoofLoad: z.number().nullable().optional(),
  grossCombinationWeight: z.number().nullable().optional(),
  lengthCategory: z.string().nullable().optional(),
  heightCategory: z.string().nullable().optional(),
  cabinType: z.string().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Additional Fields
// ----------------------------------------------------------------------------
export const BaseVehicleAdditionalSchema = z.object({
  primaryKeyCount: z.number().nullable().optional(),
  otherRecords: z.string().nullable().optional(),
  customFields: z.array(FieldValueSchema).optional(),
});

// ============================================================================
// BASE VEHICLE COMPLETE SCHEMAS
// ============================================================================

// ----------------------------------------------------------------------------
// Base Vehicle Response Schema (for GET operations)
// ----------------------------------------------------------------------------
export const BaseVehicleResponseSchema = BaseVehicleIdentificationSchema.extend(
  BaseVehicleMetadataSchema.shape
)
  .extend(BaseVehicleBasicInfoSchema.shape)
  .extend(BaseVehicleTechnicalSpecsSchema.shape)
  .extend(BaseVehicleEngineSpecsSchema.shape)
  .extend(BaseVehicleConsumptionSchema.shape)
  .extend(BaseVehicleElectricSpecsSchema.shape)
  .extend(BaseVehicleAppearanceSchema.shape)
  .extend(BaseVehicleRegistrationSchema.shape)
  .extend(BaseVehicleServiceSchema.shape)
  .extend(BaseVehicleInspectionSchema.shape)
  .extend(BaseVehicleWarrantySchema.shape)
  .extend(BaseVehicleCommercialSchema.shape)
  .extend(BaseVehicleAdditionalSchema.shape);

// ----------------------------------------------------------------------------
// Base Vehicle Request Schema (for CREATE/UPDATE operations)
// ----------------------------------------------------------------------------
export const BaseVehicleRequestSchema = BaseVehicleBasicInfoSchema.extend(
  BaseVehicleTechnicalSpecsSchema.shape
)
  .extend(BaseVehicleEngineSpecsSchema.shape)
  .extend(BaseVehicleConsumptionSchema.shape)
  .extend(BaseVehicleElectricSpecsSchema.shape)
  .extend(BaseVehicleAppearanceSchema.shape)
  .extend(BaseVehicleRegistrationSchema.shape)
  .extend(BaseVehicleServiceSchema.shape)
  .extend(BaseVehicleInspectionSchema.shape)
  .extend(BaseVehicleWarrantySchema.shape)
  .extend(BaseVehicleCommercialSchema.shape)
  .extend(BaseVehicleAdditionalSchema.shape)
  .extend({
    // Convert some fields to use VehicleDataDecimal for requests
    carbonDioxideEmission: VehicleDataDecimalSchema.nullable().optional(),
    carbonDioxideEmissionDischarged: VehicleDataDecimalSchema.nullable().optional(),
    carbonDioxideEmissionWeightedCombined: VehicleDataDecimalSchema.nullable().optional(),
    // NEDC consumption fields
    fuelConsumptionCombined: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionExtraUrban: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionUrban: VehicleDataDecimalSchema.nullable().optional(),
    // WLTP consumption fields (liquid/gas)
    fuelConsumptionWltpCombined: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionWltpCityCenter: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionWltpOutskirts: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionWltpCountryRoads: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionWltpHighway: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionWltpWeighted: VehicleDataDecimalSchema.nullable().optional(),
    // WLTP consumption fields (electric)
    fuelConsumptionWltpCombinedKwh: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionWltpCityCenterKwh: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionWltpOutskirtsKwh: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionWltpCountryRoadsKwh: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionWltpHighwayKwh: VehicleDataDecimalSchema.nullable().optional(),
    fuelConsumptionWltpWeightedCombinedKwh: VehicleDataDecimalSchema.nullable().optional(),
    // Power consumption fields
    powerConsumptionCombined: VehicleDataDecimalSchema.nullable().optional(),
    powerCombustionEngine: VehicleDataDecimalSchema.nullable().optional(),
    powerElectricEngine: VehicleDataDecimalSchema.nullable().optional(),
    loadingArea: VehicleDataDecimalSchema.nullable().optional(),
    // Use request-specific schemas
    dimensions: DimensionsRequestBodySchema.nullable().optional(),
    dimensionsLoadingSpace: DimensionsRequestBodySchema.nullable().optional(),
    exteriorColor: ColorRequestBodySchema.nullable().optional(),
    interiorColor: ColorRequestBodySchema.nullable().optional(),
    features: z.array(FeatureRequestBodySchema).nullable().optional(),
    electricVehicleBattery: ElectricVehicleBatteryRequestBodySchema.nullable().optional(),
  });

// ============================================================================
// CURRENT IMPLEMENTATION (Vehicle Operations API)
// ============================================================================
// Note: These are the current schemas used by the existing API.
// They extend the base vehicle schemas above.

// ----------------------------------------------------------------------------
// Legacy Aliases (for backward compatibility)
// ----------------------------------------------------------------------------
export const VehicleV2ResponseBodySchema = BaseVehicleResponseSchema;

// ----------------------------------------------------------------------------
// Update Request Schema (PUT operations)
// ----------------------------------------------------------------------------
export const UpdateVehicleRequestBodySchema = BaseVehicleRequestSchema.extend({
  title: z.string().nullable().optional(),
  keyLocation: z.string().nullable().optional(),
  make: z.string(), // Required in update
  modelFamily: z.string(), // Required in update
  type: VehicleTypeEnumSchema, // Required in update
  vehicleSourceCodeId: z.string().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Create Request Schema (POST operations)
// ----------------------------------------------------------------------------
export const CreateVehicleRequestBodySchema = BaseVehicleRequestSchema.extend({
  externalId: z.string().nullable().optional(),
  title: z.string().nullable().optional(),
  vehicleId: z.string().nullable().optional(),
  make: z.string(), // Required in create
  modelFamily: z.string(), // Required in create
  type: VehicleTypeEnumSchema, // Required in create
  vehicleSourceCodeId: z.string().nullable().optional(),
});

// ----------------------------------------------------------------------------
// Create Response Schema
// ----------------------------------------------------------------------------
export const CreateVehicleResponseBodySchema = z.object({
  id: z.string(),
});

export const FiscalPowerResponseBodySchema = z.object({
  fiscalPower: z.number().int().nullable(),
  engineVolume: z.number().int(),
});

// ============================================================================
// API SCHEMAS
// ============================================================================

// ----------------------------------------------------------------------------
// API Response Schemas
// ----------------------------------------------------------------------------
export const GetVehicleApiResponseSchema = VehicleV2ResponseBodySchema; // status 200
export const UpdateVehicleApiResponseSchema = z.void(); // status 204
export const CreateVehicleApiResponseSchema = CreateVehicleResponseBodySchema; // status 201
export const CalculateFiscalPowerApiResponseSchema = FiscalPowerResponseBodySchema; // status 200

// ----------------------------------------------------------------------------
// API Arguments Schemas
// ----------------------------------------------------------------------------
export const GetVehicleApiArgSchema = z.object({
  vehicleId: z.string(),
});

export const UpdateVehicleApiArgSchema = z.object({
  vehicleId: z.string(),
  validateMandatoryFields: z.boolean().nullish().optional(),
  updateVehicleRequestBody: UpdateVehicleRequestBodySchema,
});

export const CreateVehicleApiArgSchema = z.object({
  createVehicleRequestBody: CreateVehicleRequestBodySchema,
  validateMandatoryFields: z.boolean().nullish().optional(),
});

export const CalculateFiscalPowerApiArgSchema = z.object({
  engineVolume: z.number().int(),
});

// ============================================================================
// EXPORTED TYPES
// ============================================================================

// ----------------------------------------------------------------------------
// Base Types
// ----------------------------------------------------------------------------
export type VehicleDataDecimal = z.infer<typeof VehicleDataDecimalSchema>;
export type ValueType = z.infer<typeof ValueTypeSchema>;
export type VehicleTypeEnum = z.infer<typeof VehicleTypeEnumSchema>;
export type FuelTypeEnum = z.infer<typeof FuelTypeEnumSchema>;
export type HybridTypeEnum = z.infer<typeof HybridTypeEnumSchema>;
export type ConditionEnum = z.infer<typeof ConditionEnumSchema>;
export type Co2EmissionClassEnum = z.infer<typeof Co2EmissionClassEnumSchema>;

// ----------------------------------------------------------------------------
// Component Types
// ----------------------------------------------------------------------------
export type DimensionsV2ResponseBody = z.infer<typeof DimensionsV2ResponseBodySchema>;
export type DimensionsRequestBody = z.infer<typeof DimensionsRequestBodySchema>;
export type ColorV2ResponseBody = z.infer<typeof ColorV2ResponseBodySchema>;
export type ColorRequestBody = z.infer<typeof ColorRequestBodySchema>;
export type VehicleFeatureV2ResponseBody = z.infer<typeof VehicleFeatureV2ResponseBodySchema>;
export type FeatureRequestBody = z.infer<typeof FeatureRequestBodySchema>;

// ----------------------------------------------------------------------------
// Service Interval Types
// ----------------------------------------------------------------------------
export type VehicleServiceIntervalsServiceInspectionVehicleOperations = z.infer<
  typeof VehicleServiceIntervalsServiceInspectionSchemaVehicleOperations
>;
export type VehicleServiceIntervalsEngineOilVehicleOperations = z.infer<
  typeof VehicleServiceIntervalsEngineOilSchemaVehicleOperations
>;
export type VehicleServiceIntervalsGearboxOilVehicleOperations = z.infer<
  typeof VehicleServiceIntervalsGearboxOilSchemaVehicleOperations
>;
export type VehicleServiceIntervalsFourByFourOilDistributorVehicleOperations = z.infer<
  typeof VehicleServiceIntervalsFourByFourOilDistributorSchemaVehicleOperations
>;
export type VehicleServiceIntervalsTimingBeltVehicleOperations = z.infer<
  typeof VehicleServiceIntervalsTimingBeltSchemaVehicleOperations
>;
export type VehicleServiceIntervalsBrakeFluidVehicleOperations = z.infer<
  typeof VehicleServiceIntervalsBrakeFluidSchemaVehicleOperations
>;
export type VehicleServiceIntervalsCoolantFluidVehicleOperations = z.infer<
  typeof VehicleServiceIntervalsCoolantFluidSchemaVehicleOperations
>;
export type VehicleServiceIntervalsAnnualMileageVehicleOperations = z.infer<
  typeof VehicleServiceIntervalsAnnualMileageSchemaVehicleOperations
>;
export type VehicleServiceIntervalsVehicleOperations = z.infer<
  typeof VehicleServiceIntervalsSchemaVehicleOperations
>;

// ----------------------------------------------------------------------------
// Field Value Types
// ----------------------------------------------------------------------------
export type TypedAnyValue = z.infer<typeof TypedAnyValueSchema>;
export type FieldValue = z.infer<typeof FieldValueSchema>;

// ----------------------------------------------------------------------------
// Electric Vehicle Types
// ----------------------------------------------------------------------------
export type ElectricVehicleBatteryV2ResponseBody = z.infer<
  typeof ElectricVehicleBatteryV2ResponseBodySchema
>;
export type ElectricVehicleBatteryRequestBody = z.infer<
  typeof ElectricVehicleBatteryRequestBodySchema
>;

// ----------------------------------------------------------------------------
// Base Vehicle Types
// ----------------------------------------------------------------------------
export type BaseVehicleResponse = z.infer<typeof BaseVehicleResponseSchema>;
export type BaseVehicleRequest = z.infer<typeof BaseVehicleRequestSchema>;

// ----------------------------------------------------------------------------
// Current Implementation Type (legacy)
// ----------------------------------------------------------------------------
export type VehicleV2ResponseBody = z.infer<typeof VehicleV2ResponseBodySchema>;
export type UpdateVehicleRequestBody = z.infer<typeof UpdateVehicleRequestBodySchema>;
export type CreateVehicleResponseBody = z.infer<typeof CreateVehicleResponseBodySchema>;
export type CreateVehicleRequestBody = z.infer<typeof CreateVehicleRequestBodySchema>;
export type FiscalPowerResponseBody = z.infer<typeof FiscalPowerResponseBodySchema>;

// ----------------------------------------------------------------------------
// API Types
// ----------------------------------------------------------------------------
export type GetVehicleApiResponse = z.infer<typeof GetVehicleApiResponseSchema>;
export type UpdateVehicleApiResponse = z.infer<typeof UpdateVehicleApiResponseSchema>;
export type CreateVehicleApiResponse = z.infer<typeof CreateVehicleApiResponseSchema>;
export type CalculateFiscalPowerApiResponse = z.infer<typeof CalculateFiscalPowerApiResponseSchema>;
export type GetVehicleApiArg = z.infer<typeof GetVehicleApiArgSchema>;
export type UpdateVehicleApiArg = z.infer<typeof UpdateVehicleApiArgSchema>;
export type CreateVehicleApiArg = z.infer<typeof CreateVehicleApiArgSchema>;
export type CalculateFiscalPowerApiArg = z.infer<typeof CalculateFiscalPowerApiArgSchema>;

// ████████████████████████████████████████████████████████████████████████████
// ████████████████████████████████████████████████████████████████████████████
// ██                                                                        ██
// ██                        SALE VEHICLE TYPES                              ██
// ██                                                                        ██
// ████████████████████████████████████████████████████████████████████████████
// ████████████████████████████████████████████████████████████████████████████

export const VehicleSourceCodeIdSchema = z.string().nullable();

// ----------------------------------------------------------------------------
// Register Vehicle for Sale Schemas
// ----------------------------------------------------------------------------
export const SaleVehiclePhotoSchema = z.object({
  fileId: z.string(),
  category: z.enum(['INTERIOR', 'EXTERIOR']).nullable().optional(),
});

export const RegisterVehicleForSaleV2RequestBodySchema = z.object({
  salePresetId: z.string().nullable().optional(),
  vehicleSourceCodeId: VehicleSourceCodeIdSchema.nullable().optional(),
  photos: z.array(SaleVehiclePhotoSchema).nullable().optional(),
  targetAlbum: z.enum(['PHOTOS_CAR_SALES', 'PHOTOS_CAR_PURCHASE']).nullable().optional(),
});

export const RegisterVehicleForSaleV2ApiArgSchema = z.object({
  vehicleId: z.string(),
  registerVehicleForSaleV2RequestBody: RegisterVehicleForSaleV2RequestBodySchema,
});

export const RegisterVehicleForSaleV2ApiResponseSchema = z.void(); // status 204

// ----------------------------------------------------------------------------
// Register Vehicle for Sale Types
// ----------------------------------------------------------------------------
export type RegisterVehicleForSaleV2ApiResponse = z.infer<
  typeof RegisterVehicleForSaleV2ApiResponseSchema
>;
export type RegisterVehicleForSaleV2RequestBody = z.infer<
  typeof RegisterVehicleForSaleV2RequestBodySchema
>;
export type RegisterVehicleForSaleV2ApiArg = z.infer<typeof RegisterVehicleForSaleV2ApiArgSchema>;

// ----------------------------------------------------------------------------
// Register Vehicle To Buy (Purchase) Schemas
// ----------------------------------------------------------------------------
export const RegisterVehicleToBuyRequestBodySchema = z.object({
  salePresetId: z.string().nullable().optional(),
  vehicleSourceCodeId: VehicleSourceCodeIdSchema.nullable().optional(),
  photos: z.array(SaleVehiclePhotoSchema).nullable().optional(),
  targetAlbum: z.enum(['PHOTOS_CAR_SALES', 'PHOTOS_CAR_PURCHASE']).nullable().optional(),
});

export const RegisterVehicleToBuyApiArgSchema = z.object({
  vehicleId: z.string(),
  registerVehicleToBuyRequestBody: RegisterVehicleToBuyRequestBodySchema,
});

export const RegisterVehicleToBuyApiResponseSchema = z.void(); // status 204

// ----------------------------------------------------------------------------
// Register Vehicle To Buy Types
// ----------------------------------------------------------------------------
export type RegisterVehicleToBuyApiResponse = z.infer<typeof RegisterVehicleToBuyApiResponseSchema>;
export type RegisterVehicleToBuyRequestBody = z.infer<typeof RegisterVehicleToBuyRequestBodySchema>;
export type RegisterVehicleToBuyApiArg = z.infer<typeof RegisterVehicleToBuyApiArgSchema>;

export const PatchSaleVehicleSourceCodeRequestBodySchema = z.object({
  vehicleSourceCodeId: VehicleSourceCodeIdSchema.nullable().optional(),
});

export const PatchSaleVehicleSourceCodeApiResponseSchema = z.void(); // status 204
export const PatchSaleVehicleSourceCodeApiArgSchema = z.object({
  vehicleId: z.string(),
  patchSaleVehicleSourceCodeRequestBody: PatchSaleVehicleSourceCodeRequestBodySchema,
});

export type PatchSaleVehicleSourceCodeApiResponse = z.infer<
  typeof PatchSaleVehicleSourceCodeApiResponseSchema
>;
export type PatchSaleVehicleSourceCodeApiArg = z.infer<
  typeof PatchSaleVehicleSourceCodeApiArgSchema
>;

// ----------------------------------------------------------------------------
// Sale Vehicle Photo Types
// ----------------------------------------------------------------------------
export type SaleVehiclePhoto = z.infer<typeof SaleVehiclePhotoSchema>;

// ----------------------------------------------------------------------------
// Clone Sale Vehicle Schemas
// ----------------------------------------------------------------------------
export const CloneSaleVehicleRequestBodySchema = z.object({
  toVehicleId: z.string(),
  salePresetId: z.string().nullable().optional(),
  vehicleSourceCodeId: VehicleSourceCodeIdSchema.nullable().optional(),
  photos: z.array(SaleVehiclePhotoSchema).nullable().optional(),
  targetAlbum: z.enum(['PHOTOS_CAR_SALES', 'PHOTOS_CAR_PURCHASE']).nullable().optional(),
});

export const CloneSaleVehicleApiArgSchema = z.object({
  vehicleId: z.string(),
  cloneSaleVehicleRequestBody: CloneSaleVehicleRequestBodySchema,
});

export const CloneSaleVehicleApiResponseSchema = z.void(); // status 204

// ----------------------------------------------------------------------------
// Clone Sale Vehicle Types
// ----------------------------------------------------------------------------
export type CloneSaleVehicleRequestBody = z.infer<typeof CloneSaleVehicleRequestBodySchema>;
export type CloneSaleVehicleApiArg = z.infer<typeof CloneSaleVehicleApiArgSchema>;
export type CloneSaleVehicleApiResponse = z.infer<typeof CloneSaleVehicleApiResponseSchema>;
